import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { IndexStyle } from "./IndexStyle";
import { useEffect, useMemo, useRef } from "react";
import { IndexResponsiveStyle } from "./IndexResponsiveStyle";
import { useIsMobile } from "../hooks/useIsMobile";
import Navbar from "./Navbar";
const Index = ({ weather, country }) => {
  const isMobile = useIsMobile();
  const isCountryResolved = Boolean(country);
  const isJapan = country === "JP";

  const { i18n, t } = useTranslation();

  const languageTheme = i18n.language === "ja" ? "theme-japan" : "theme-nordic";
  const weatherTheme = weather ? `weather-${weather}` : "";

  let darkMode = useSelector((state) => state.theme.darkMode);
  const isNightScene = darkMode;
  const heroBaseName =
    i18n.language === "ja"
      ? `japan-${isNightScene ? "night" : "day"}`
      : `nordic-${isNightScene ? "night" : "day"}`;
  const heroImage = `/img/hero/${heroBaseName}.jpg`;
  const heroImageWidths = [640, 1280, 1920, 2560];
  const heroSrcSet = (format) =>
    heroImageWidths
      .map((width) => `/img/hero/${heroBaseName}-${width}.${format} ${width}w`)
      .join(", ");

  const titleJapaneseRef = useRef(null);
  const titleFrontendRef = useRef(null);

  useEffect(() => {
    const titleJapanese = titleJapaneseRef.current;
    const titleFrontend = titleFrontendRef.current;
    if (!titleJapanese || !titleFrontend) return;

    let ticking = false;
    const updateTitlePosition = () => {
      const scrollPosition = window.scrollY;
      titleJapanese.style.transform = isMobile
        ? "none"
        : `translateY(-${scrollPosition * 2}px)`;
      titleJapanese.style.willChange = isMobile ? "auto" : "transform";
      titleFrontend.style.transform = isMobile
        ? "none"
        : `translateX(-${scrollPosition * 2}px)`;
      titleFrontend.style.willChange = isMobile ? "auto" : "transform";
      ticking = false;
    };

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateTitlePosition);
    };

    updateTitlePosition();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isMobile]);

  const stars = useMemo(() => {
    if (weather !== "night") return [];
    return Array.from({ length: 150 }).map(() => ({
      top: `${(Math.random() * 100).toFixed(2)}%`,
      left: `${(Math.random() * 100).toFixed(2)}%`,
      size: `${(Math.random() * 2 + 1).toFixed(2)}px`,
      glow: `${(Math.random() * 50 + 30).toFixed(0)}%`,
      opacity: (Math.random() * 0.8 + 0.2).toFixed(2),
      duration: `${(Math.random() * 5 + 3).toFixed(2)}s`,
      delay: `${(-Math.random() * 5).toFixed(2)}s`,
    }));
  }, [weather]);

  const drops = useMemo(() => {
    if (weather !== "rainy" && weather !== "heavy-rain" && weather !== "snow") {
      return [];
    }
    return Array.from({ length: 200 }).map(() => ({
      opacity: (Math.random() * 0.9).toFixed(2),
      left: `${(Math.random() * 120).toFixed(2)}vw`,
      borderLeftWidth: `${(Math.random() * 8).toFixed(2)}px`,
      duration: `${(0.5 + Math.random() * 1.5).toFixed(2)}s`,
      delay: `${(-Math.random() * 3).toFixed(2)}s`,
    }));
  }, [weather]);

  return (
    <IndexStyle
      className={`${darkMode ? "dark" : ""} ${languageTheme} ${weatherTheme} section`}
    >
      <IndexResponsiveStyle>
        <Navbar />
        <div className="main-wrapper">
          <div className={`theme-bg ${languageTheme}`} aria-hidden="true">
            <picture>
              <source
                type="image/avif"
                srcSet={heroSrcSet("avif")}
                sizes="100vw"
              />
              <source
                type="image/webp"
                srcSet={heroSrcSet("webp")}
                sizes="100vw"
              />
              <img
                src={heroImage}
                alt=""
                data-hero-image="true"
                {...{ fetchpriority: "high" }}
                loading="eager"
                decoding="async"
                sizes="100vw"
              />
            </picture>
          </div>
          <div
            className={`main ${weather || ""} ${isNightScene ? "night" : ""}`}
            id="home"
          >
            <div className={`weather ${weather ? `${weather} show` : ""}`}>
              {weather === "night" && (
                <div className="stars">
                  {stars.map((s, i) => (
                    <div
                      className="star"
                      key={i}
                      style={{
                        top: s.top,
                        left: s.left,
                        width: s.size,
                        height: s.size,
                        background: `radial-gradient(circle, #fff ${s.glow}, transparent 100%)`,
                        opacity: s.opacity,
                        animationDuration: s.duration,
                        animationDelay: s.delay,
                      }}
                    ></div>
                  ))}
                </div>
              )}
              {(weather === "rainy" ||
                weather === "heavy-rain" ||
                weather === "snow") && (
                <div className="rain">
                  {drops.map((d, i) => (
                    <div
                      key={i}
                      className="drop"
                      style={{
                        opacity: d.opacity,
                        left: d.left,
                        borderLeftWidth: d.borderLeftWidth,
                        animationDuration: d.duration,
                        animationDelay: d.delay,
                      }}
                    ></div>
                  ))}
                </div>
              )}
            </div>
            <div className="title-wrapper">
              <div className="title-name">
                <h1 className="title-english">
                  <div className="title-english-1">MINELLA</div>
                  <div className="title-english-2">RESUME</div>
                </h1>
                <p
                  ref={titleJapaneseRef}
                  className="title-japanese title-colors"
                >
                  履歴書
                </p>
              </div>

              <ul className="title-icon-list">
                <li>
                  <img
                    src={`/img/icon-next${!darkMode ? "-black" : ""}.svg`}
                    alt="next"
                    loading="lazy"
                  />
                </li>
                <li>
                  <img src="/img/icon-react.svg" alt="react" loading="lazy" />
                </li>
                <li>
                  <img
                    src="/img/icon-tailwind.svg"
                    alt="tailwind"
                    loading="lazy"
                  />
                </li>
                <li>
                  <img
                    src="/img/icon-typescript.svg"
                    alt="typescript"
                    loading="lazy"
                  />
                </li>
                <li>
                  <img src="/img/icon-nodejs.svg" alt="nodejs" loading="lazy" />
                </li>
                <li>
                  <img
                    src="/img/icon-mongodb.svg"
                    alt="mongodb"
                    loading="lazy"
                  />
                </li>
                <li>
                  <img src="/img/icon-prisma.svg" alt="prisma" loading="lazy" />
                </li>
                <li>
                  <img src="/img/icon-aws-app.svg" alt="azure" loading="lazy" />
                </li>
              </ul>
              <div className="title-frontend-wrapper">
                <h2
                  ref={titleFrontendRef}
                  className="title-frontend title-colors"
                >
                  frontend developer &#128168;
                </h2>
              </div>
            </div>
            <div className="right-frame">
              <div className="info-card">
                <dl className="info-table">
                  <div className="info-row">
                    <dt>
                      <span className="info-icon">📍</span>
                      {t("location")}
                    </dt>
                    <dd>{t("locationValue")}</dd>
                  </div>

                  {isCountryResolved && !isJapan && (
                    <div className="info-row">
                      <dt>
                        <span className="info-icon">??</span>
                        {t("openToRelocate")}
                      </dt>
                      <dd>{t("relocationCountries")}</dd>
                    </div>
                  )}
                  <div className="info-row">
                    <dt>
                      <span className="info-icon">{isJapan ? "JP" : "EU"}</span>
                      {t("workAuthorization")}
                    </dt>
                    <dd>
                      {isCountryResolved && (
                        <div className="work-auth">
                          {isJapan ? (
                            <div>
                              {t("dependentVisa")}
                              <br />
                              {t("dependentVisaUntil")}
                            </div>
                          ) : (
                            <>
                              <div>{t("citizenshipInProgress")}</div>

                              <a
                                href="https://servizipst.giustizia.it/PST/it/pst_2_6_1.wp?actionPath=/ExtStr2/do/consultazionepubblica/sicid/contenzioso/detail.action&currentFrame=10&idfascicolo=1710338&numeroregistro=00024521&annoregistro=2024&regioneRicerca=20&ufficioRicerca=0270420098&registroRicerca=CC"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {t("processNumber")} #24521/2024
                              </a>

                              <div>{t("requiresSponsorship")}</div>
                            </>
                          )}
                        </div>
                      )}
                    </dd>
                  </div>

                  <div className="info-row">
                    <dt>
                      <span className="info-icon">🌐</span>
                      {t("languages")}
                    </dt>

                    <dd>
                      <div className="languages">
                        <span>
                          <img alt="us" src="img/icon-us.svg" loading="lazy" />
                          {t("fluent")}
                        </span>
                        <span>
                          <img
                            alt="sweden"
                            src="img/flags/se.svg"
                            loading="lazy"
                          />
                          A1
                        </span>
                        <span>
                          <img
                            alt="japan"
                            src="img/icon-japan.svg"
                            loading="lazy"
                          />
                          JLPT N2
                        </span>
                        <span>
                          <img
                            alt="spain"
                            src="img/icon-es.svg"
                            loading="lazy"
                          />
                          {t("business")}
                        </span>
                        <span>
                          <img
                            alt="brazil"
                            src="img/icon-brazil.svg"
                            loading="lazy"
                          />
                          {t("native")}
                        </span>
                      </div>
                    </dd>
                  </div>
                </dl>

                <div className="documents">
                  <a
                    className="document-item"
                    href="/files/paulo_minella_resume.doc"
                    download
                  >
                    <img src="/img/icon-word.svg" alt="" loading="lazy" />
                    <span>
                      {t("resume")} - {t("english")}
                    </span>
                    <svg
                      className="doc-arrow"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        d="M7 17L17 7M17 7H8M17 7V16"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </a>

                  <a
                    className="document-item"
                    href="/files/ミネラパウロエンヒケ_履歴書.xlsx"
                    download
                  >
                    <img src="/img/icon-excel.svg" alt="" loading="lazy" />
                    <span>
                      {t("resume")} - {t("japanese")}
                    </span>
                    <svg
                      className="doc-arrow"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        d="M7 17L17 7M17 7H8M17 7V16"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="header-footer">
            <div className="education-item">
              <div className="education-item-logo">
                <img
                  src="img/icon-unisul.svg"
                  alt="unisul"
                  className="unisul-logo"
                  loading="lazy"
                />
              </div>
              <div className="education-item-details">
                <p className="education-title">UNISUL</p>
                <ul>
                  <li>&#129417; {t("bachelorIt")}</li>
                  <li>&#128197; 07/2004 - 02/2009</li>
                </ul>
              </div>
            </div>
            <div className="education-item">
              <div className="education-item-logo">
                <img
                  className="icon-ala"
                  src="img/icon-ala.svg"
                  alt="unisul"
                  loading="lazy"
                />
              </div>
              <div className="education-item-details">
                <p className="education-title">Academy of Language Arts</p>
                <ul>
                  <li>&#129417; {t("japaneseSchool")}</li>
                  <li>&#128197; 01/2018 - 12/2019</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </IndexResponsiveStyle>
    </IndexStyle>
  );
};

export default Index;
